import {ActivityIndicator, Modal, Pressable, StyleSheet, Text, View, ViewStyle} from "react-native";
import {UserAvatar} from "@/lib/types";
import {Color} from "@/lib/styles";
import Avatar from "@/lib/components/Avatar";
import Icon from "@/lib/components/Icon";
import {formatSince, uuidV7ToDate} from "@/lib/time";
import {useAccount} from "@/lib/providers/AccountProvider";
import {chat} from "@/lib/proto";
import Mosaic from "@/lib/components/Mosaic";
import {Reactions, useChat} from "@/lib/providers/ChatProvider";
import {useCallback, useMemo, useState} from "react";
import ChatReaction from "@/lib/components/ChatReaction";
import ChatMessage = chat.ChatMessage;
import ProtoAvatar = chat.Avatar;
import SendChatMessageRequest = chat.SendChatMessageRequest;

type Props = {
    message: ChatMessage | SendChatMessageRequest,
    reactions: Reactions,
    onPressThumbnail?: (thumbnailID: string) => any,
}

// const emojiList = ["👍", "😄", "♥️", "😍", "😥", "😠", "🚀", "🧑🏼‍🐰‍🧑🏿"];
const emojiList = ["👍", "😄", "♥️", "😍", "😥", "😠", "🚀"];

export default function ChatBubble({message, reactions, onPressThumbnail}: Props) {
    const {account} = useAccount();
    const {sendChatMessage} = useChat();

    const [hover, setHover] = useState(false);
    const [showMenu, setShowMenu] = useState(false);

    const chatMessage = message as ChatMessage;
    const pendingMessage = message as SendChatMessageRequest;

    const avatar: UserAvatar | undefined = useMemo(() => {
        if (!!chatMessage.messageId && !!chatMessage.sender) {
            return {
                id: chatMessage.sender.id!,
                username: chatMessage.sender.username!,
                color: chatMessage.sender.color!,
                contrast_color: chatMessage.sender.contrastColor!,
                gecos: chatMessage.sender.gecos!,
                avatar_url: chatMessage.sender.avatarUrl || null,
            }
        }
        return account?.avatar;
    }, [chatMessage, account]);

    const timestamp = uuidV7ToDate(message.clientMessageId) || new Date();
    const isMyMessage = avatar?.id === account?.avatar.id;
    const alignSelf: ViewStyle = isMyMessage ? {
        justifyContent: "flex-start",
        flexDirection: "row-reverse",
    } : {};
    const backgroundColor: ViewStyle = {backgroundColor: avatar?.color + '40'}; // 25% opacity
    const thumbnailIds = (message as ChatMessage)?.thumbnailIds || [];
    const bottomPadding: ViewStyle = thumbnailIds.length > 0 && message.text.length === 0 && reactions.size === 0 ? {paddingBottom: 0} : {};

    const reactionEntries = useMemo(() => {
        const result: [string, ProtoAvatar[]][] = [];
        reactions.forEach((avatars, emoji) => {
            if (avatars && avatars.length > 0) {
                result.push([emoji, avatars]);
            }
        });
        return result;
    }, [reactions]);

    const reactWithEmoji = useCallback((emoji: string) => {
        sendChatMessage(SendChatMessageRequest.create({
            chatId: message.chatId,
            inReplyTo: (message as ChatMessage).messageId,
            reaction: emoji,
        }));
        setShowMenu(false);
    }, [message, sendChatMessage]);

    if (!avatar) {
        return null;
    }

    return (
        <View
            style={[styles.container, alignSelf]}
            onPointerEnter={() => setHover(true)}
            onPointerLeave={() => setHover(false)}
            // onLayout={() => console.log("layout", message.clientMessageId)}
        >
            <Pressable
                style={[styles.messageBubble, backgroundColor, bottomPadding]}
                onLongPress={() => setShowMenu(true)}
            >

                <View style={styles.header}>
                    <View style={styles.sender}>
                        <Avatar avatar={avatar} pressable={false} borderColor={avatar.color}/>
                        <Text style={[styles.senderGecos]}>{avatar.gecos}</Text>
                    </View>
                    <View style={styles.timestamp}>
                        <Icon kind={"clock"} size={12} color={Color.PrimaryText}/>
                        <Text style={{fontSize: 12, color: Color.PrimaryText}}>{formatSince(new Date(timestamp))}</Text>
                    </View>
                </View>

                <Mosaic thumbnailIds={thumbnailIds} onPressThumbnail={onPressThumbnail}/>

                {pendingMessage.attachments?.length > 0 &&
                    <ActivityIndicator size={"small"} color={Color.PrimaryText}/>
                }

                {message.text.length > 0 &&
                    <Text style={[styles.text]}>{message.text}</Text>
                }

                {/*{chatMessage.messageId === undefined && !!pendingMessage.reaction &&*/}
                {/*    <Text style={[styles.text]}>{pendingMessage.reaction}</Text>*/}
                {/*}*/}

                {reactionEntries.length > 0 &&
                    <View style={styles.reactionContainer}>
                        {reactionEntries.map(([emoji, avatars]) => (
                            <ChatReaction
                                key={emoji}
                                emoji={emoji}
                                avatars={avatars}
                                onPress={() => reactWithEmoji(emoji)}
                            />
                        ))}
                    </View>
                }
            </Pressable>

            {hover &&
                <View style={styles.contextHover}>
                    <Pressable style={styles.ellipsis} onPress={() => setShowMenu(true)}>
                        <Icon kind={"ellipsis"} size={16}/>
                    </Pressable>
                </View>
            }

            <Modal
                visible={showMenu}
                allowSwipeDismissal={true}
                transparent={true}
                onDismiss={() => setShowMenu(false)}
                onRequestClose={() => setShowMenu(false)}
                animationType={"slide"}
                presentationStyle={"overFullScreen"}
            >
                <Pressable style={{flex: 1}} onPress={() => setShowMenu(false)}>
                    <View style={styles.contextMenu}>

                        <View style={styles.emojiList}>
                            {emojiList.map(emoji => (
                                <Pressable key={emoji} onPress={() => reactWithEmoji(emoji)}>
                                    <Text style={styles.emoji}>{emoji}</Text>
                                </Pressable>
                            ))}
                        </View>

                        <View style={[styles.messageBubble, backgroundColor, {
                            minWidth: undefined,
                            maxWidth: undefined,
                            width: "100%"
                        }]}>

                            <View style={styles.header}>
                                <View style={styles.sender}>
                                    <Avatar avatar={avatar} pressable={false} borderColor={avatar.color}/>
                                    <Text style={[styles.senderGecos]}>{avatar.gecos}</Text>
                                </View>
                                <View style={styles.timestamp}>
                                    <Icon kind={"clock"} size={12} color={Color.PrimaryText}/>
                                    <Text style={{
                                        fontSize: 12,
                                        color: Color.PrimaryText
                                    }}>{formatSince(new Date(timestamp))}</Text>
                                </View>
                            </View>

                            <Mosaic thumbnailIds={thumbnailIds} onPressThumbnail={onPressThumbnail}/>

                            {message.text.length > 0 &&
                                <Text style={[styles.text]} numberOfLines={5}>{message.text}</Text>
                            }

                        </View>

                        {
                            /*
                        <Header title={"Spam tools"}/>

                        <Nav>
                            <ActionButton onPress={() => {
                                setShowMenu(false);
                                for (let step = 1000; step > 0; step--) {
                                    sendChatMessage(SendChatMessageRequest.create({
                                        chatId: message.chatId,
                                        text: `${step} bokser med øl på en hylle, ${step} bokser med øl! Vi knekker en, drikker den, ${step - 1} bokser igjen!`
                                    }));
                                }
                            }} title={"Send 1000 meldinger"}/>
                            <ActionButton onPress={() => {
                                setShowMenu(false);
                                const emojRange = [
                                    [128513, 128591],
                                    [9986, 10160],
                                    [128640, 128704]
                                ];
                                for (let i = 0; i < emojRange.length; i++) {
                                    const range = emojRange[i];
                                    for (let x = range[0]; x < range[1]; x++) {
                                        reactWithEmoji(String.fromCodePoint(x));
                                    }
                                }
                            }} title={"Send mange emojis"}/>
                        </Nav>
                             */
                        }

                    </View>
                </Pressable>
            </Modal>

        </View>
    );
}

export const styles = StyleSheet.create({
    container: {
        width: "100%",
        flexDirection: "row",
        justifyContent: "flex-start",
        alignItems: "flex-end",
        gap: 8,
    },
    contextHover: {
        //
    },
    ellipsis: {
        borderRadius: 64,
        borderColor: Color.PrimaryText,
        borderWidth: 1,
        padding: 8,
    },
    contextMenu: {
        width: "90%",
        maxWidth: 640,
        padding: 16,
        borderRadius: 16,
        gap: 16,
        backgroundColor: Color.BackgroundContainer,
        margin: "auto",
        overflow: "hidden",
    },
    emojiList: {
        flexDirection: "row",
        flexWrap: "wrap",
        gap: 8,
    },
    emoji: {
        fontSize: 24,
    },
    messageBubble: {
        flexDirection: "column",
        flexWrap: "wrap",
        paddingTop: 10,
        paddingBottom: 10,
        gap: 10,
        borderRadius: 16,
        alignSelf: "flex-start",
        minWidth: "40%",
        maxWidth: "80%",
        overflow: "hidden",
    },
    header: {
        flexDirection: "row",
        flexWrap: "nowrap",
        justifyContent: "space-between",
        alignItems: "center",
        paddingHorizontal: 10,
        gap: 16,
        width: "100%",
    },
    sender: {
        flexDirection: "row",
        flexWrap: "nowrap",
        alignItems: "center",
        gap: 8,
        flexShrink: 1,
    },
    senderGecos: {
        fontSize: 16,
        fontWeight: 500,
        color: Color.PrimaryText,
        flexWrap: "wrap",
    },
    timestamp: {
        flexDirection: "row",
        flexWrap: "nowrap",
        alignItems: "center",
        gap: 4,
        fontSize: 12,
        color: Color.SecondaryText,
    },
    text: {
        width: "100%",
        flexWrap: "wrap",
        flexShrink: 1,
        fontSize: 16,
        paddingHorizontal: 10,
        color: Color.PrimaryText,
    },
    reactionContainer: {
        flexDirection: "row",
        flexWrap: "wrap",
        alignSelf: "flex-end",
        gap: 6,
        paddingHorizontal: 10,
    },
})